import * as React from 'react'
import { Deal } from 'types'

// --- helpers ---
const toArray = <T>(v?: T | T[]) => (Array.isArray(v) ? v : v ? [v] : [])
const isOwnedBy = (owner: string | string[] | undefined, userId?: string) => !!userId && toArray(owner).includes(userId)

const getDealId = (d: any) => d?.SK ?? d?.id ?? `${d?.PK ?? ''}#${d?.SK ?? ''}`

type ApproverLike = {
  id?: string
  user_uuid?: string
  name?: string
  image?: string
  hasApproved?: boolean
  hasRejected?: boolean
  [k: string]: any
}
type UserLike = {
  id: string
  name?: string
  image?: string
  [k: string]: any
}

const approverId = (a: ApproverLike | string): string | undefined =>
  typeof a === 'string' ? a : a?.id ?? a?.user_uuid ?? a?.userId ?? a?.uid

const toUserLike = (a: ApproverLike | string): UserLike | null => {
  const id = approverId(a)
  if (!id) return null
  if (typeof a === 'string') return { id }
  return { id, name: a?.name, image: a?.image }
}

const norm = (s?: string) => (s || '').trim()

// Allowed/visible buckets for stage & status
const STAGE_LABELS = ['Deal Won', 'Contract Sent', 'Proposal', 'Contract Created', 'Pending Approval', 'Cold'] as const

const STATUS_LABELS = ['Active', 'In Progress', '30 Day Notice', 'Replaced', 'Pending', 'Cancelled', 'Ended'] as const

type StageKey = typeof STAGE_LABELS[number]
type StatusKey = typeof STATUS_LABELS[number]

// --- return type ---
type DealBuckets = {
  // role-ish groupings
  byOwner: Deal[]
  byApprover: Deal[] // deals where viewUserId is in approvers
  byTeam: Deal[] // deals where viewUserId is in team_details

  anyMatch: Deal[] // union of the three above (deduped)
  allMatch: Deal[] // intersection: owner & approver & team

  byStage: Record<StageKey, Deal[]>
  byStatus: Record<StatusKey, Deal[]>

  // approvers
  approversByDeal: Map<string, UserLike[]> // dealId -> approver users (best-effort)
  approverUsersUnique: UserLike[] // unique users across all deals’ approvers
}

export function useMyDeals(deals: Deal[] | undefined, viewUserId?: string) {
  return React.useMemo<DealBuckets>(() => {
    const byStage = STAGE_LABELS.reduce((m, k) => ({ ...m, [k]: [] as Deal[] }), {} as Record<StageKey, Deal[]>)
    const byStatus = STATUS_LABELS.reduce((m, k) => ({ ...m, [k]: [] as Deal[] }), {} as Record<StatusKey, Deal[]>)

    const byOwner: Deal[] = []
    const byApprover: Deal[] = []
    const byTeam: Deal[] = []
    const anyMatch: Deal[] = []
    const allMatch: Deal[] = []

    const approversByDeal = new Map<string, UserLike[]>()
    const approverSet = new Map<string, UserLike>() // id -> user

    const seenAny = new Set<string>()

    for (const d of deals ?? []) {
      const id = getDealId(d)

      // ---- owner / team ----
      const ownerSource = (d as any)?.owner ?? []
      const isOwner = isOwnedBy(ownerSource, viewUserId)

      const team_details: any[] = Array.isArray((d as any)?.team_details) ? (d as any).team_details : []
      const teamIds = team_details.map(u => (u?.user_uuid || u?.id || u?.uid || '').toString()).filter(Boolean)
      const onTeam = !!viewUserId && teamIds.includes(viewUserId)

      // ---- approvers ----
      const rawApprovers = Array.isArray((d as any)?.approvers) ? (d as any).approvers : []
      const approverUsers = rawApprovers.map(toUserLike).filter((u): u is UserLike => !!u && !!u.id)

      // track per deal
      approversByDeal.set(id, approverUsers)
      // track globally unique
      for (const u of approverUsers) {
        if (!approverSet.has(u.id)) approverSet.set(u.id, u)
      }

      const iAmApprover = !!viewUserId && approverUsers.some(u => u.id === viewUserId)

      if (isOwner) byOwner.push(d)
      if (iAmApprover) byApprover.push(d)
      if (onTeam) byTeam.push(d)

      const hits = (isOwner ? 1 : 0) + (iAmApprover ? 1 : 0) + (onTeam ? 1 : 0)
      if (hits > 0 && !seenAny.has(id)) {
        anyMatch.push(d)
        seenAny.add(id)
      }
      if (hits === 3) {
        allMatch.push(d)
      }

      // ---- stage buckets ----
      const stage = norm((d as any)?.stage)
      switch (stage) {
        case 'Deal Won':
        case 'Contract Sent':
        case 'Proposal':
        case 'Contract Created':
        case 'Pending Approval':
        case 'Cold':
          byStage[stage as StageKey].push(d)
          break
        default:
          // silently ignore non-listed stages
          break
      }

      // ---- status buckets ----
      const status = norm((d as any)?.dealStatus)
      switch (status) {
        case 'Active':
        case 'In Progress':
        case '30 Day Notice':
        case 'Replaced':
        case 'Pending':
        case 'Cancelled':
        case 'Ended':
          byStatus[status as StatusKey].push(d)
          break
        default:
          // silently ignore non-listed statuses
          break
      }
    }

    return {
      byOwner,
      byApprover,
      byTeam,
      anyMatch,
      allMatch,
      byStage,
      byStatus,
      approversByDeal,
      approverUsersUnique: Array.from(approverSet.values()),
    }
  }, [deals, viewUserId])
}
